import { D, MON_NAME, dim, fmtYM, nextBiz, parseD, prevBiz, ymAdd, ymd } from './dates.ts';
import type { CustomDeadline, Settings } from './types.ts';

export type DeadlineKind = 'docs' | 'zus' | 'tax' | 'vat' | 'env' | 'ksef' | 'custom';

export interface Deadline {
  id: string;
  kind: DeadlineKind;
  title: string;
  form: string;
  sub: string;
  note: string;
  due: Date;
  dueS: string;
  orig: Date;
  shifted: boolean;
  /** Termin sprzed rozpoczęcia korzystania z aplikacji – traktowany jako zamknięty */
  before: boolean;
  customId?: string;
}

type Shift = 'next' | 'prev' | 'none';

/**
 * Terminy cykliczne JDG: czynny podatnik VAT (przelew VAT, JPK składa biuro), ryczałt, ZUS opłacany tylko za siebie,
 * plus BDO, KSeF i terminy własne. Termin w weekend lub święto przesuwa się na następny dzień roboczy
 * (art. 12 § 5 Ordynacji podatkowej); wewnętrzny termin dla biura – na poprzedni dzień roboczy.
 */
export function genDeadlines(
  st: Settings,
  fromYM: string,
  toYM: string,
  custom: Record<string, CustomDeadline> = {},
  opts: { rodoReviewedAt?: string } = {},
): Deadline[] {
  const out: Deadline[] = [];
  const T = st.toggles;
  const mk = (id: string, kind: DeadlineKind, title: string, form: string, sub: string, orig: Date, shift: Shift, note = ''): Deadline => {
    const due = shift === 'next' ? nextBiz(orig) : shift === 'prev' ? prevBiz(orig) : orig;
    return { id, kind, title, form, sub, note, due, dueS: ymd(due), orig, shifted: ymd(due) !== ymd(orig), before: false };
  };

  for (let p = ymAdd(fromYM, -1); p <= toYM; p = ymAdd(p, 1)) {
    const n = ymAdd(p, 1);
    const ny = +n.slice(0, 4), nm = +n.slice(5), pm = +p.slice(5), lbl = `${MON_NAME[pm - 1]} ${p.slice(0, 4)}`;
    if (T.docs)
      out.push(mk(`docs-${p}`, 'docs', 'Dokumenty do biura', 'biuro', `za ${lbl}${st.accountantName ? ', ' + st.accountantName : ''}`,
        D(ny, nm, Math.min(+st.docsDay || 5, dim(ny, nm))), 'prev', 'Faktury sprzedaży i zakupu, w tym zagraniczne z odwrotnym obciążeniem.'));
    out.push(mk(`zus-${p}`, 'zus', 'Składki ZUS', 'DRA', `społeczne i zdrowotna za ${lbl}`, D(ny, nm, 20), 'next',
      pm === 4 ? 'W tej DRA jest też roczne rozliczenie składki zdrowotnej.' : ''));
    out.push(mk(`ryczalt-${p}`, 'tax', 'Ryczałt', 'mikrorachunek', `za ${lbl}`, D(ny, nm, 20), 'next',
      pm === 12 ? 'Za grudzień: do 20 stycznia, chyba że wcześniej złożysz PIT-28 i zapłacisz z zeznaniem.' : ''));
    // Identyfikator „jpk-” zostaje dla zgodności z odhaczonymi terminami i wydarzeniami w Kalendarzu.
    if (T.vat !== false) out.push(mk(`jpk-${p}`, 'vat', 'Przelew VAT', 'mikrorachunek', `za ${lbl}`, D(ny, nm, 25), 'next', 'Deklarację JPK_V7M składa biuro rachunkowe.'));
    if (T.infakt) {
      const py = +p.slice(0, 4);
      out.push(mk(`infakt-${p}`, 'docs', 'Dokumenty w inFakcie', 'inFakt', `faktury i dokumenty wewnętrzne za ${lbl}`, D(py, pm, dim(py, pm)), 'prev',
        'Faktury do kontrahentów, faktura wewnętrzna sprzedaży bez faktur i dowód wewnętrzny.'));
    }
    if (T.vatue)
      out.push(mk(`vatue-${p}`, 'vat', 'Informacja podsumowująca', 'VAT-UE', `za ${lbl}`, D(ny, nm, 25), 'next',
        'Tylko przy WDT, WNT lub usługach sprzedanych firmom z UE. Import usług tu nie trafia.'));
    if (T.oss && pm % 3 === 0) {
      const q = pm / 3;
      out.push(mk(`oss-${p.slice(0, 4)}-Q${q}`, 'vat', 'Deklaracja OSS', 'VIU-DO', `za ${q}. kwartał ${p.slice(0, 4)}`, D(ny, nm, dim(ny, nm)), 'none',
        'Termin OSS nie przesuwa się na dzień roboczy.'));
    }
  }

  const y0 = +fromYM.slice(0, 4), y1 = +toYM.slice(0, 4);
  for (let y = y0; y <= y1 + 1; y++) {
    if (T.bdo) {
      out.push(mk(`bdo-fee-${y}`, 'env', 'Opłata roczna BDO', 'BDO', `za ${y}`, D(y, 2, dim(y, 2)), 'next',
        'Nieopłacona opłata roczna oznacza wykreślenie z rejestru.'));
      out.push(mk(`bdo-rep-${y}`, 'env', 'Sprawozdanie BDO o opakowaniach', 'BDO', `za ${y - 1}`, D(y, 3, 15), 'next',
        'Razem z ewentualną opłatą produktową. Poniżej 1 tony opakowań rocznie dołącz dokumenty pomocy de minimis.'));
    }
    out.push(mk(`pit28-${y}`, 'tax', 'Zeznanie roczne', 'PIT-28', `za ${y - 1}`, D(y, 4, 30), 'next'));
    out.push(mk(`zdrow-${y}`, 'zus', 'Roczne rozliczenie składki zdrowotnej', 'DRA', `za ${y - 1}, w DRA za kwiecień`, D(y, 5, 20), 'next',
      'Ewentualną dopłatę wpłać razem ze składkami za kwiecień.'));
  }
  const rev = parseD(opts.rodoReviewedAt);
  if (T.rodo && rev) {
    // Przegląd rejestru RODO co rok – identyfikator od roku przeglądu, żeby kolejny przegląd tworzył nowy termin.
    const due = new Date(rev.getFullYear() + 1, rev.getMonth(), rev.getDate());
    out.push(mk(`rodo-${ymd(rev)}`, 'docs', 'Przegląd rejestru czynności przetwarzania', 'RODO', `ostatni przegląd ${fmtYM(ymd(rev).slice(0, 7))}`, due, 'next',
      'Sprawdź, czy doszły nowe narzędzia, dostawcy lub cele przetwarzania, i zaktualizuj umowy powierzenia.'));
  }
  if (T.ksef)
    out.push(mk('ksef-2027', 'ksef', 'Faktury B2B tylko w KSeF', 'KSeF', 'obowiązkowo od 1.01.2027', D(2026, 12, 31), 'none',
      'Kończy się limit 10 000 zł brutto miesięcznie na faktury poza KSeF. Faktury dla konsumentów mogą zostać poza KSeF.'));

  for (const [id, c] of Object.entries(custom)) {
    const base = parseD(c.date);
    if (!base || !c.title) continue;
    const mkc = (sfx: string, dt: Date, sub: string) => {
      const o = mk(`c-${id}${sfx}`, 'custom', c.title, 'własny', sub, dt, 'none', c.note || '');
      o.customId = id;
      out.push(o);
    };
    if (c.repeat === 'monthly') {
      for (let p = fromYM; p <= toYM; p = ymAdd(p, 1)) {
        const y = +p.slice(0, 4), m = +p.slice(5);
        if (p < ymd(base).slice(0, 7)) continue;
        mkc(`-${p}`, D(y, m, Math.min(base.getDate(), dim(y, m))), 'co miesiąc');
      }
    } else if (c.repeat === 'yearly') {
      for (let y = Math.max(y0, base.getFullYear()); y <= y1 + 1; y++) {
        const m = base.getMonth() + 1;
        mkc(`-${y}`, D(y, m, Math.min(base.getDate(), dim(y, m))), 'co roku');
      }
    } else mkc('', base, 'jednorazowo');
  }

  const from = D(+fromYM.slice(0, 4), +fromYM.slice(5), 1);
  const to = D(+toYM.slice(0, 4), +toYM.slice(5), dim(+toYM.slice(0, 4), +toYM.slice(5)));
  const start = parseD(st.startDate);
  return out
    .filter((x) => x.due >= from && x.due <= to)
    .map((x) => ({ ...x, before: !!(start && x.due < start) }))
    .sort((a, b) => a.due.getTime() - b.due.getTime() || a.title.localeCompare(b.title, 'pl'));
}
